import React, { useEffect, useState } from 'react';
import { useTranslatedText } from '../hooks/useTranslation';
import { useI18n } from '../contexts/i18n-context';

interface TranslatedTextProps {
  text: string;
  fallback?: string;
  as?: keyof JSX.IntrinsicElements;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Component that automatically translates text
 * Usage: <TranslatedText text="Hello World" />
 */
export function TranslatedText({ 
  text, 
  fallback, 
  as: Component = 'span',
  className,
  children 
}: TranslatedTextProps) {
  const { language } = useI18n();
  const translated = useTranslatedText(text, fallback);
  const [forceUpdate, setForceUpdate] = useState(0);
  
  // Listen for language changes to force re-render
  useEffect(() => {
    const handleLanguageChange = () => {
      setForceUpdate(prev => prev + 1);
    };
    
    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);
  
  return (
    <Component className={className}>
      {translated}
      {children}
    </Component>
  );
}

/**
 * Higher-order component to translate props
 */
export function withTranslation<P extends { text?: string }>(
  Component: React.ComponentType<P>
) {
  return function TranslatedComponent(props: P) {
    const { useTranslatedText } = require('../hooks/useTranslation');
    const translatedText = props.text ? useTranslatedText(props.text) : undefined;
    
    return <Component {...props} text={translatedText} />;
  };
}

